import { mat4, vec3 } from 'gl-matrix';

import type { InteractionEvent } from '../../interaction/types';
import type { IViewport } from '../../viewports/types';
import type { ITool } from '../types';
import { FrameCoalescer } from './frame-coalescer';

/** Default rotation factor (degrees per normalized viewport unit). */
const ROTATION_FACTOR = 360;

/**
 * Camera trackball rotation tool — orbits the camera around the focal point.
 *
 * Follows the Cornerstone3D TrackballRotateTool approach:
 *   1. Normalize mouse delta to viewport size
 *   2. Horizontal delta → azimuth rotation around viewUp
 *   3. Vertical delta → elevation rotation around the right vector
 *   4. Build a single combined 4x4 matrix and transform position, focalPoint,
 *      and viewUp atomically
 *
 * This avoids the gimbal-lock and view-up snapping issues caused by
 * sequential camera.azimuth()/camera.elevation()/orthogonalizeViewUp() calls.
 */
export class RotateTool implements ITool {
    static readonly toolName = 'Rotate';

    readonly id = RotateTool.toolName;
    readonly name = 'Rotate';
    readonly cursor = 'grab';
    readonly completed = false;

    private pendingDx = 0;
    private pendingDy = 0;
    private pendingViewport: IViewport | null = null;
    private coalescer = new FrameCoalescer(() => this.applyPending());

    activate(_viewport: IViewport): void {}

    deactivate(): void {
        this.coalescer.cancel();
        this.resetPending();
    }

    cancel(): void {
        this.coalescer.cancel();
        this.resetPending();
    }

    handleEvent(event: InteractionEvent): boolean {
        if (event.type === 'mouse_drag_end') {
            this.coalescer.flush();
            return true;
        }

        if (event.type !== 'mouse_drag') return false;

        const [dx, dy] = event.deltaPoints.canvas;
        this.pendingDx += dx;
        this.pendingDy += dy;
        this.pendingViewport = event.viewport;
        this.coalescer.schedule();
        return true;
    }

    private applyPending(): void {
        const vp = this.pendingViewport;
        const dx = this.pendingDx;
        const dy = this.pendingDy;
        this.resetPending();

        if (vp && (dx !== 0 || dy !== 0)) {
            this.rotate(vp, dx, dy);
            vp.render();
        }
    }

    private resetPending(): void {
        this.pendingDx = 0;
        this.pendingDy = 0;
        this.pendingViewport = null;
    }

    private rotate(viewport: IViewport, dx: number, dy: number): void {
        const renderer = viewport.getRenderer(0);
        const camera = renderer.getActiveCamera();
        const canvas = viewport.getCanvas();
        if (!canvas) return;

        const { width, height } = canvas.getBoundingClientRect();
        if (width === 0 || height === 0) return;

        const cameraPos = camera.getPosition() as [number, number, number];
        const cameraFp = camera.getFocalPoint() as [number, number, number];
        const cameraViewUp = camera.getViewUp() as [number, number, number];

        // Center of rotation = focal point
        const center = vec3.fromValues(cameraFp[0], cameraFp[1], cameraFp[2]);

        // Scale rotation sensitivity by zoom level: when zoomed in (small viewAngle),
        // reduce rotation speed proportionally to prevent jerky movement.
        // Use tan-based scaling to match the actual magnification ratio.
        const DEFAULT_VIEW_ANGLE = 30;
        const viewAngle = camera.getViewAngle();
        const zoomScale = camera.getParallelProjection()
            ? 1
            : Math.tan((viewAngle * Math.PI) / 360) / Math.tan((DEFAULT_VIEW_ANGLE * Math.PI) / 360);

        // Compute rotation angles from mouse deltas (normalized by viewport size)
        const azimuthAngle = ((-ROTATION_FACTOR * zoomScale * dx) / width) * (Math.PI / 180);
        const elevationAngle = ((-ROTATION_FACTOR * zoomScale * dy) / height) * (Math.PI / 180);

        // View-up axis for azimuth rotation
        const viewUp = vec3.fromValues(cameraViewUp[0], cameraViewUp[1], cameraViewUp[2]);

        // Compute right vector for elevation rotation:
        // right = cross(directionOfProjection, viewUp)
        const dop = vec3.fromValues(
            cameraFp[0] - cameraPos[0],
            cameraFp[1] - cameraPos[1],
            cameraFp[2] - cameraPos[2]
        );
        vec3.normalize(dop, dop);
        const right = vec3.create();
        vec3.cross(right, dop, viewUp);
        vec3.normalize(right, right);

        // Build combined rotation matrix:
        //   Translate(center) × RotateAzimuth(viewUp) × RotateElevation(right) × Translate(-center)
        const transform = mat4.create();
        mat4.translate(transform, transform, center);
        mat4.rotate(transform, transform, azimuthAngle, viewUp);
        mat4.rotate(transform, transform, elevationAngle, right);
        const negCenter = vec3.negate(vec3.create(), center);
        mat4.translate(transform, transform, negCenter);

        // Transform camera position
        const newPos = vec3.fromValues(cameraPos[0], cameraPos[1], cameraPos[2]);
        vec3.transformMat4(newPos, newPos, transform);

        // Transform focal point
        const newFp = vec3.fromValues(cameraFp[0], cameraFp[1], cameraFp[2]);
        vec3.transformMat4(newFp, newFp, transform);

        // Transform view-up: transform the point (cameraPos + viewUp) then subtract new position
        const viewUpPoint = vec3.fromValues(
            cameraPos[0] + cameraViewUp[0],
            cameraPos[1] + cameraViewUp[1],
            cameraPos[2] + cameraViewUp[2]
        );
        vec3.transformMat4(viewUpPoint, viewUpPoint, transform);
        const newViewUp = vec3.sub(vec3.create(), viewUpPoint, newPos);
        vec3.normalize(newViewUp, newViewUp);

        // Apply all camera properties atomically
        camera.setPosition(newPos[0], newPos[1], newPos[2]);
        camera.setFocalPoint(newFp[0], newFp[1], newFp[2]);
        camera.setViewUp(newViewUp[0], newViewUp[1], newViewUp[2]);
        camera.orthogonalizeViewUp();

        viewport.updateCameraClippingRange();
    }
}
